import type { AircraftType3D, AircraftPhysicalDimensions } from '@/services/3d/AircraftLoaderService'

export interface DemoAircraftSpec {
  type: AircraftType3D
  dims: AircraftPhysicalDimensions
  displayName: string
  manufacturer: string
  model: string
  /** hex color used for label badge */
  badgeColor: string
}

// Real model_url values sourced from aircraft_types at KDET location (d639ad3a-2c25-4988-ad92-0b3695219287)
const M = {
  kingAir350:         'https://pub-662d8c9d9d90415ca1ed65700178d475.r2.dev/models/2fc70036-beechcraft-king-air-350/beechcraft-king-air-350-lod1.glb',
  superKingAir:       'https://pub-662d8c9d9d90415ca1ed65700178d475.r2.dev/models/3ec36e41-beechcraft-super-king-air/beechcraft-super-king-air-lod1.glb',
  challenger350:      'https://pub-662d8c9d9d90415ca1ed65700178d475.r2.dev/models/c69bcb32-bombardier-challenger-350/bombardier-challenger-350-lod1.glb',
  challenger300:      'https://pub-662d8c9d9d90415ca1ed65700178d475.r2.dev/models/da8abf97-bombardier-challenger-300/bombardier-challenger-300-lod1.glb',
  global6000:         'https://pub-662d8c9d9d90415ca1ed65700178d475.r2.dev/models/326b4329-bombardier-global-6000/bombardier-global-6000-lod1.glb',
  citationXls:        'https://pub-662d8c9d9d90415ca1ed65700178d475.r2.dev/models/7d7fdc4a-cessna-citation-xls/cessna-citation-xls-lod1.glb',
  citationLatitude:   'https://pub-662d8c9d9d90415ca1ed65700178d475.r2.dev/models/375bc202-cessna-citation-latitude/cessna-citation-latitude-lod1.glb',
  citationLongitude:  'https://pub-662d8c9d9d90415ca1ed65700178d475.r2.dev/models/8028f9e8-cessna-citation-longitude/cessna-citation-longitude-lod1.glb',
  mooneyM20:          'https://pub-662d8c9d9d90415ca1ed65700178d475.r2.dev/models/ac29264f-mooney-m20/mooney-m20-lod1.glb',
}

// ---------------------------------------------------------------------------
// Fleet aircraft (8 aircraft that permanently reside in the hangar)
// ---------------------------------------------------------------------------

export const FLEET_SPECS: Record<string, DemoAircraftSpec> = {
  'king-air-350': {
    displayName: 'King Air 350',
    manufacturer: 'Beechcraft',
    model: 'King Air 350',
    badgeColor: '#6366f1',
    type: { id: 'type-be35', typeCode: 'BE35', modelSettings: { filePath: M.kingAir350 } },
    dims: { length: 46.8, wingspan: 57.9, height: 14.4, tailHeight: 14.4, category: 'turboprop', gearType: 'tricycle' },
  },
  'citation-xls': {
    displayName: 'Citation XLS+',
    manufacturer: 'Cessna',
    model: 'Citation XLS+',
    badgeColor: '#0ea5e9',
    type: { id: 'type-cxls', typeCode: 'CXLS', modelSettings: { filePath: M.citationXls } },
    dims: { length: 52.5, wingspan: 56.3, height: 15.6, tailHeight: 15.6, category: 'midsize_jet', gearType: 'tricycle' },
  },
  'phenom-300': {
    displayName: 'Phenom 300E',
    manufacturer: 'Embraer',
    model: 'Phenom 300E',
    badgeColor: '#10b981',
    // Closest available: Citation Latitude (light jet, similar footprint)
    type: { id: 'type-e55p', typeCode: 'E55P', modelSettings: { filePath: M.citationLatitude } },
    dims: { length: 51.1, wingspan: 52.2, height: 17.0, tailHeight: 17.0, category: 'light_jet', gearType: 'tricycle' },
  },
  'challenger-350': {
    displayName: 'Challenger 350',
    manufacturer: 'Bombardier',
    model: 'Challenger 350',
    badgeColor: '#f59e0b',
    type: { id: 'type-bd10', typeCode: 'BD10', modelSettings: { filePath: M.challenger350 } },
    dims: { length: 68.8, wingspan: 68.8, height: 20.8, tailHeight: 20.8, category: 'super_midsize_jet', gearType: 'tricycle' },
  },
  'citation-cj4': {
    displayName: 'Citation CJ4',
    manufacturer: 'Cessna',
    model: 'Citation CJ4',
    badgeColor: '#8b5cf6',
    // Closest available: Citation XLS (same family, slightly larger)
    type: { id: 'type-c25c', typeCode: 'C25C', modelSettings: { filePath: M.citationXls } },
    dims: { length: 53.3, wingspan: 50.8, height: 15.5, tailHeight: 15.5, category: 'light_jet', gearType: 'tricycle' },
  },
  'pilatus-pc12': {
    displayName: 'Pilatus PC-12',
    manufacturer: 'Pilatus',
    model: 'PC-12 NGX',
    badgeColor: '#ec4899',
    // Closest available: Beechcraft Super King Air (turboprop, similar size class)
    type: { id: 'type-pc12', typeCode: 'PC12', modelSettings: { filePath: M.superKingAir } },
    dims: { length: 47.3, wingspan: 53.0, height: 14.1, tailHeight: 14.1, category: 'turboprop', gearType: 'tricycle' },
  },
  'gulfstream-g280': {
    displayName: 'Gulfstream G280',
    manufacturer: 'Gulfstream',
    model: 'G280',
    badgeColor: '#14b8a6',
    // Closest available: Citation Longitude (super-midsize jet, similar range class)
    type: { id: 'type-gl2t', typeCode: 'GL2T', modelSettings: { filePath: M.citationLongitude } },
    dims: { length: 66.3, wingspan: 63.4, height: 22.1, tailHeight: 22.1, category: 'super_midsize_jet', gearType: 'tricycle' },
  },
  'hondajet': {
    displayName: 'HondaJet Elite II',
    manufacturer: 'Honda Aircraft',
    model: 'HondaJet Elite II',
    badgeColor: '#f97316',
    // Closest available: Mooney M20 (compact piston, smallest footprint available)
    type: { id: 'type-ha4t', typeCode: 'HA4T', modelSettings: { filePath: M.mooneyM20 } },
    dims: { length: 42.6, wingspan: 39.8, height: 14.9, tailHeight: 14.9, category: 'very_light_jet', gearType: 'tricycle' },
  },
}

// ---------------------------------------------------------------------------
// Hero aircraft — Bombardier Global 7500 (the "arriving VIP")
// ---------------------------------------------------------------------------

export const HERO_SPEC: DemoAircraftSpec = {
  displayName: 'Global 7500',
  manufacturer: 'Bombardier',
  model: 'Global 7500',
  badgeColor: '#f59e0b',
  // Best available: Global 6000 (same family, visually near-identical)
  type: { id: 'type-gl7t', typeCode: 'GL7T', modelSettings: { filePath: M.global6000 } },
  dims: { length: 111.0, wingspan: 104.0, height: 27.3, tailHeight: 27.3, category: 'ultra_long_range', gearType: 'tricycle' },
}
